import { DataStream } from "@/core/exif/data-stream"
import { displayP3, sRGB } from "@/core/exif/icc-profiles"
import { PixelData, RGBPlanesDataLayout } from "@/core/utils/image-data"

type ScratchBufferProvider = (byteLength: number) => Uint8Array

export interface PSDScratchBufferOptions {
	width: number
	height: number
	bitDepth: 8 | 16
	targetChunkByteLength?: number
}

export interface PSDEncodeOptions {
	ppi?: number
	targetChunkByteLength?: number
	scratchBufferProvider?: ScratchBufferProvider
}

export interface EncodedPSDData {
	type: typeof PSD_MIME_TYPE
	size: number
	writeTo(
		writable: Pick<FileSystemWritableFileStream, "write">,
		options?: { signal?: AbortSignal },
	): Promise<void>
	toBlob(): Promise<Blob>
}

export interface PSDEncoderLike {
	encodePSD(pixelData: PixelData, options?: PSDEncodeOptions): Promise<Blob>
	encodePSDData?(pixelData: PixelData, options?: PSDEncodeOptions): Promise<EncodedPSDData>
}

const PSD_MIME_TYPE = "image/vnd.adobe.photoshop"
const PSD_CHANNELS = 3
const DEFAULT_TARGET_CHUNK_BYTE_LENGTH = 8 * 1024 * 1024
const PSD_FIXED_HEADER_BYTE_LENGTH = 26
const PSD_RESOURCE_RESOLUTION_INFO = 0x03ed
const PSD_RESOURCE_ICC_PROFILE = 0x040f

export function getPSDBufferByteLengths(options: PSDScratchBufferOptions) {
	if (options.width <= 0 || options.height <= 0) {
		return { pixelByteLength: 0, scratchByteLength: 0 }
	}

	const bytesPerSample = options.bitDepth >> 3
	const pixelByteLength = options.width * options.height * PSD_CHANNELS * bytesPerSample
	const targetChunkByteLength = options.targetChunkByteLength ?? DEFAULT_TARGET_CHUNK_BYTE_LENGTH
	const scratchByteLength =
		options.bitDepth === 16
			? Math.min(pixelByteLength, evenByteLength(targetChunkByteLength))
			: 0

	return {
		pixelByteLength,
		scratchByteLength,
	}
}

export async function encodePSD(
	pixelData: PixelData,
	options: PSDEncodeOptions = {},
): Promise<Blob> {
	return await encodePSDData(pixelData, options).toBlob()
}

export function encodePSDData(
	pixelData: PixelData,
	options: PSDEncodeOptions = {},
): EncodedPSDData {
	const { data, width, height } = pixelData

	if (data instanceof Float32Array) {
		throw new Error("PSD format does not support float")
	}

	const bitDepth = data instanceof Uint16Array ? 16 : 8
	const bytesPerSample = bitDepth >> 3
	const rgbPlanesDataLayout = pixelData.rgbPlanesDataLayout
	const planeByteLength = width * height * bytesPerSample

	if (!rgbPlanesDataLayout) {
		throw new Error("PSD export requires RGB plane data")
	}

	validateRGBPlanesLayout(data, rgbPlanesDataLayout, width, height, bytesPerSample)

	const imageByteLength = width * height * PSD_CHANNELS * bytesPerSample
	const header = new Uint8Array(createPSDHeader(pixelData, bitDepth, options))
	const scratchByteLength =
		bitDepth === 16
			? Math.min(
					imageByteLength,
					evenByteLength(
						options.targetChunkByteLength ?? DEFAULT_TARGET_CHUNK_BYTE_LENGTH,
					),
				)
			: 0

	return new ChunkedPSDData({
		header,
		pixelData: { data, rgbPlanesDataLayout, planeByteLength },
		bitDepth,
		size: header.byteLength + imageByteLength,
		scratchByteLength,
		scratchBufferProvider:
			options.scratchBufferProvider ?? createFixedScratchBufferProvider(scratchByteLength),
	})
}

function createPSDHeader(pixelData: PixelData, bitsPerChannel: 8 | 16, options: PSDEncodeOptions) {
	const iccProfile = getPSDICCProfile(pixelData.colorSpace)
	const imageResourcesByteLength =
		getResourceBlockByteLength(16) +
		(iccProfile ? getResourceBlockByteLength(iccProfile.byteLength) : 0)
	const headerByteLength = PSD_FIXED_HEADER_BYTE_LENGTH + 4 + 4 + imageResourcesByteLength + 4 + 2
	const headerBuffer = new ArrayBuffer(headerByteLength)
	const headerView = new DataView(headerBuffer)
	const headerStream = new DataStream(headerView, { littleEndian: false })

	headerStream.setText("8BPS")
	headerStream.setUint16(1)

	for (let i = 0; i < 6; i++) {
		headerStream.setUint8(0)
	}

	headerStream.setUint16(PSD_CHANNELS)
	headerStream.setUint32(pixelData.height)
	headerStream.setUint32(pixelData.width)
	headerStream.setUint16(bitsPerChannel)
	headerStream.setUint16(3)

	writeSection(headerStream, 2)

	writeSection(headerStream, 2, () => {
		writeResourceBlock(headerStream, PSD_RESOURCE_RESOLUTION_INFO, 16, () => {
			const ppi = options.ppi || 72
			const hRes = Math.round(ppi * 65536)
			const vRes = Math.round(ppi * 65536)

			headerStream.setUint32(hRes)
			headerStream.setUint16(1)
			headerStream.setUint16(1)

			headerStream.setUint32(vRes)
			headerStream.setUint16(1)
			headerStream.setUint16(1)
		})

		if (iccProfile) {
			writeResourceBlock(
				headerStream,
				PSD_RESOURCE_ICC_PROFILE,
				iccProfile.byteLength,
				() => {
					headerStream.byteView.set(iccProfile, headerStream.offset)
					headerStream.offset += iccProfile.byteLength
				},
			)
		}
	})

	writeSection(headerStream, 2)
	headerStream.setUint16(0)

	return headerBuffer
}

function getPSDICCProfile(colorSpace: PredefinedColorSpace) {
	switch (colorSpace) {
		case "display-p3":
			return displayP3
		case "srgb":
			return sRGB
	}
}

function getResourceBlockByteLength(dataByteLength: number) {
	return 4 + 2 + 2 + 4 + dataByteLength + (dataByteLength % 2)
}

function writeResourceBlock(
	dataStream: DataStream,
	resourceId: number,
	dataByteLength: number,
	writeData: () => void,
) {
	dataStream.setText("8BIM")
	dataStream.setUint16(resourceId)

	dataStream.setUint8(0)
	dataStream.setUint8(0)

	dataStream.setUint32(dataByteLength)
	writeData()

	if (dataByteLength % 2 !== 0) {
		dataStream.setUint8(0)
	}
}

function writeSection(dataStream: DataStream, round: number, writeData?: () => void) {
	const offset = dataStream.offset
	dataStream.setUint32(0)

	writeData?.()

	while (dataStream.offset % round !== 0) {
		dataStream.setUint8(0)
	}

	const currentOffset = dataStream.offset
	const length = currentOffset - offset - 4

	dataStream.offset = offset
	dataStream.setUint32(length)
	dataStream.offset = currentOffset
}

function createFixedScratchBufferProvider(byteLength: number) {
	const scratchView = new Uint8Array(new ArrayBuffer(byteLength))

	return (requestedByteLength: number) => {
		if (requestedByteLength > scratchView.byteLength) {
			throw new Error("PSD scratch buffer is smaller than the requested chunk")
		}

		return scratchView.subarray(0, requestedByteLength)
	}
}

class ChunkedPSDData implements EncodedPSDData {
	public readonly type = PSD_MIME_TYPE
	public readonly size: number

	private readonly header: Uint8Array
	private readonly pixelData: {
		data: Uint8Array | Uint16Array
		rgbPlanesDataLayout: RGBPlanesDataLayout
		planeByteLength: number
	}
	private readonly bitDepth: 8 | 16
	private readonly scratchByteLength: number
	private readonly scratchBufferProvider: ScratchBufferProvider

	constructor(options: {
		header: Uint8Array
		pixelData: {
			data: Uint8Array | Uint16Array
			rgbPlanesDataLayout: RGBPlanesDataLayout
			planeByteLength: number
		}
		bitDepth: 8 | 16
		size: number
		scratchByteLength: number
		scratchBufferProvider: ScratchBufferProvider
	}) {
		this.header = options.header
		this.pixelData = options.pixelData
		this.bitDepth = options.bitDepth
		this.size = options.size
		this.scratchByteLength = options.scratchByteLength
		this.scratchBufferProvider = options.scratchBufferProvider
	}

	public async writeTo(
		writable: Pick<FileSystemWritableFileStream, "write">,
		options?: { signal?: AbortSignal },
	): Promise<void> {
		options?.signal?.throwIfAborted()
		await writable.write(asFileSystemWriteChunk(this.header))

		for (const plane of this.getPlanes()) {
			if (this.bitDepth === 8) {
				options?.signal?.throwIfAborted()
				await writable.write(asFileSystemWriteChunk(this.getUint8PlaneChunk(plane)))
				continue
			}

			let remainingByteLength = plane.length
			let sourceByteOffset = plane.offset

			while (remainingByteLength > 0) {
				options?.signal?.throwIfAborted()
				const chunkByteLength = Math.min(remainingByteLength, this.scratchByteLength)
				const chunk = this.getUint16PlaneChunk(sourceByteOffset, chunkByteLength)
				await writable.write(asFileSystemWriteChunk(chunk))
				sourceByteOffset += chunkByteLength
				remainingByteLength -= chunkByteLength
			}
		}
	}

	public toBlob(): Promise<Blob> {
		const parts: BlobPart[] = [this.header.slice()]

		for (const plane of this.getPlanes()) {
			if (this.bitDepth === 8) {
				parts.push(this.getUint8PlaneChunk(plane) as unknown as BlobPart)
				continue
			}

			let remainingByteLength = plane.length
			let sourceByteOffset = plane.offset

			while (remainingByteLength > 0) {
				const chunkByteLength = Math.min(remainingByteLength, this.scratchByteLength)
				parts.push(this.getUint16PlaneChunk(sourceByteOffset, chunkByteLength).slice())
				sourceByteOffset += chunkByteLength
				remainingByteLength -= chunkByteLength
			}
		}

		return Promise.resolve(new Blob(parts, { type: PSD_MIME_TYPE }))
	}

	private getPlanes() {
		const { rgbPlanesDataLayout: layout } = this.pixelData
		const { planeByteLength } = this.pixelData
		return [
			{ offset: layout.rOffset, length: planeByteLength },
			{ offset: layout.gOffset, length: planeByteLength },
			{ offset: layout.bOffset, length: planeByteLength },
		]
	}

	private getUint8PlaneChunk(plane: { offset: number; length: number }) {
		const data = this.pixelData.data

		if (!(data instanceof Uint8Array)) {
			throw new Error("PSD 8-bit plane requested from non-Uint8 data")
		}

		return new Uint8Array(data.buffer, data.byteOffset + plane.offset, plane.length)
	}

	private getUint16PlaneChunk(sourceByteOffset: number, byteLength: number) {
		const data = this.pixelData.data

		if (!(data instanceof Uint16Array)) {
			throw new Error("PSD 16-bit plane requested from non-Uint16 data")
		}

		return packPSD16PlanesChunk(
			data,
			sourceByteOffset,
			byteLength,
			this.scratchBufferProvider(byteLength),
		)
	}
}

function validateRGBPlanesLayout(
	data: Uint8Array | Uint16Array,
	layout: RGBPlanesDataLayout,
	width: number,
	height: number,
	bytesPerSample: number,
) {
	const planeByteLength = width * height * bytesPerSample
	const planes = [
		{ offset: layout.rOffset, length: layout.rLength },
		{ offset: layout.gOffset, length: layout.gLength },
		{ offset: layout.bOffset, length: layout.bLength },
	]

	for (const plane of planes) {
		if (plane.offset % bytesPerSample !== 0) {
			throw new Error("RGB plane PSD export has misaligned plane offsets")
		}

		if (plane.length < planeByteLength) {
			throw new Error("RGB plane PSD export has a plane smaller than the image size")
		}

		if (plane.offset + plane.length > data.byteLength) {
			throw new Error("RGB plane PSD export layout exceeds the data buffer")
		}
	}
}

function packPSD16PlanesChunk(
	data: Uint16Array,
	sourceByteOffset: number,
	byteLength: number,
	output: Uint8Array,
) {
	if (byteLength % 2 !== 0) {
		throw new Error("PSD 16-bit chunk byte length must be even")
	}

	if (output.byteLength < byteLength) {
		throw new Error("PSD scratch buffer is smaller than the requested chunk")
	}

	const sampleCount = byteLength >> 1
	let sourceIndex = sourceByteOffset >> 1
	let targetIndex = 0

	for (let sampleIndex = 0; sampleIndex < sampleCount; sampleIndex++) {
		const value = data[sourceIndex++] ?? 0
		output[targetIndex++] = value >> 8
		output[targetIndex++] = value & 0xff
	}

	return output.subarray(0, byteLength)
}

function asFileSystemWriteChunk(data: Uint8Array): FileSystemWriteChunkType {
	return data as Uint8Array<ArrayBuffer>
}

function evenByteLength(byteLength: number) {
	return byteLength + (byteLength % 2)
}
